import React, { useContext, useEffect, useMemo, useState } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import ControlesPrevios from "./ControlesPrevios";
import LiquidacionesRegistradas from "./LiquidacionesRegistradas";
import ProcesamientoSas from "./ProcesamientoSas";
import { pagosApi } from "./pagosApi";
import "./pagos.css";

const BLOCKING_ELIGIBILITIES = new Set([
  "REVIEW_REQUIRED", "MISSING_FISERV", "UNKNOWN_COMMERCIAL_RULE", "DATA_INTEGRITY_ERROR",
]);
const EMPTY_ROWS = Object.freeze([]);
const EMPTY_SUMMARY = Object.freeze({});

const STATUS_LABELS = {
  PAYABLE_TODAY: "Elegible hoy (sin autorizar pago)",
  FUTURE_PAYMENT: "Pago futuro",
  ALREADY_PAID: "Informado como pagado",
  OVERDUE: "Vencida; requiere revisión",
  DUE_DATE_PASSED_PAYMENT_UNKNOWN: "Vencida sin evidencia de pago",
  REVIEW_REQUIRED: "Requiere revisión",
  MISSING_FISERV: "Falta fuente Fiserv",
  UNKNOWN_COMMERCIAL_RULE: "Regla comercial desconocida",
  DATA_INTEGRITY_ERROR: "Error de integridad",
  CALCULATED: "Control monetario disponible",
  PARTIAL: "Datos monetarios incompletos",
  NO_GOLDEN_FOR_VALIDATION: "Sin golden para validar",
  EXACT: "Coincide con golden",
  MISMATCH: "Diferencia contra golden",
  COMPLETED: "Conciliación completada",
  CLOVER_COMPLETE: "Fuentes Clover completas",
  CLOVER_RUNNING: "Fuentes Clover pendientes",
};

const money = new Intl.NumberFormat("es-AR", {
  style: "currency", currency: "ARS", minimumFractionDigits: 2,
});

const formatMoney = (value) => (
  typeof value === "number" && Number.isFinite(value) ? money.format(value) : "No disponible"
);

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-");
  return year && month && day ? `${day}/${month}/${year}` : raw;
};

const labelStatus = (value) => STATUS_LABELS[value] || value || "Sin estado";

const sumAvailable = (rows, field) => {
  const available = rows.filter((row) => typeof row[field] === "number" && Number.isFinite(row[field]));
  return {
    value: available.length ? available.reduce((total, row) => total + row[field], 0) : null,
    coverage: available.length,
  };
};

const commerceKey = (row) => row.merchantId || row.terminalFiserv || row.commerceName || "SIN_COMERCIO";

export const isBlockedOperation = (row) => (
  BLOCKING_ELIGIBILITIES.has(row.paymentEligibility)
  || row.paymentAmountStatus !== "CALCULATED"
  || row.amountValidationStatus === "MISMATCH"
);

export const groupByCommerce = (rows = []) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = commerceKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return Array.from(groups.entries()).map(([key, items]) => {
    const gross = sumAvailable(items, "grossAmount");
    const net = sumAvailable(items, "calculatedTotalWithDiscounts");
    return {
      key,
      name: items.find((item) => item.commerceName)?.commerceName || "Comercio sin nombre",
      merchantId: items.find((item) => item.merchantId)?.merchantId || "No informado",
      terminal: items.find((item) => item.terminalFiserv)?.terminalFiserv || "No informada",
      operations: items.length,
      gross: gross.value,
      net: net.value,
      netCoverage: net.coverage,
      blocked: items.filter(isBlockedOperation).length,
    };
  });
};

export const buildIncidents = (rows = [], summary = {}) => {
  const definitions = [
    {
      key: "unmatched", label: "Operaciones sin conciliar",
      rows: rows.filter((row) => row.resolutionStatus !== "RESOLVED" || row.matchStatus !== "MATCH"),
      reason: (row) => labelStatus(row.resolutionStatus || row.matchStatus),
    },
    {
      key: "advance", label: "Excepciones de anticipo",
      rows: rows.filter((row) => row.scheduleType === "ANTICIPO_EXCEPTION" || row.requiresCommercialExceptionRule),
      reason: (row) => row.commercialRuleKnown ? "Regla de excepción identificada" : "Regla de excepción no confirmada",
    },
    {
      key: "mapping", label: "Problemas de mapeo",
      rows: rows.filter((row) => (
        (row.warnings || []).some((warning) => String(warning).includes("MAPPING"))
        || row.paymentEligibility === "MISSING_FISERV"
      )),
      reason: () => "No se pudo resolver el vínculo Fiserv/Clover",
    },
    {
      key: "difference", label: "Diferencias de importes",
      rows: rows.filter((row) => row.amountValidationStatus === "MISMATCH"),
      reason: (row) => `Diferencia contra golden: ${formatMoney(row.amountDifference)}`,
    },
    {
      key: "sources", label: "Fuentes faltantes",
      rows: rows.filter((row) => (
        row.paymentAmountStatus === "NO_GOLDEN_FOR_VALIDATION"
        || (row.warnings || []).includes("FISERV_COMPONENTS_INCOMPLETE")
      )),
      reason: (row) => row.paymentAmountStatus === "NO_GOLDEN_FOR_VALIDATION"
        ? "No existe fila de control en base_dashboard"
        : "Componentes Fiserv incompletos",
    },
    {
      key: "blocked", label: "Operaciones bloqueadas o a revisar",
      rows: rows.filter(isBlockedOperation),
      reason: (row) => row.eligibilityReason || labelStatus(row.paymentAmountStatus || row.paymentEligibility),
    },
  ];

  if ((summary.duplicateAnticipoRows || 0) > 0) {
    definitions.push({
      key: "duplicates", label: "Operaciones duplicadas", rows: [],
      count: summary.duplicateAnticipoRows,
      generalReason: `${summary.duplicateAnticipoRows} filas de anticipo duplicadas en ${summary.duplicateAnticipoKeys || 0} claves.`,
    });
  }
  return definitions;
};

const EmptyState = ({ children }) => (
  <div className="pagos-empty" role="status">
    <span className="pagos-empty__icon" aria-hidden="true">i</span><p>{children}</p>
  </div>
);

const SummaryCard = ({ label, value, detail, source }) => (
  <div className="pagos-summary-card">
    <span>{label}</span><strong>{value}</strong><small>{detail}</small>{source && <em>{source}</em>}
  </div>
);

const SourceBadge = ({ children, golden = false }) => (
  <span className={`pagos-source${golden ? " pagos-source--golden" : ""}`}>{children}</span>
);

export default function Pagos() {
  const { darkMode } = useContext(DarkModeContext);
  const token = getToken();
  const [canal, setCanal] = useState("SAS");
  const [fecha, setFecha] = useState("");
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [persistencia, setPersistencia] = useState(null);
  const [registros, setRegistros] = useState(0);

  useEffect(() => {
    let vigente = true;
    Promise.resolve().then(() => pagosApi.estadoLiquidaciones(token))
      .then((estado) => { if (vigente) setPersistencia(estado || { habilitada: false }); })
      .catch((requestError) => { if (vigente) setPersistencia({ habilitada: false, motivo: requestError?.message }); });
    return () => { vigente = false; };
  }, [token]);

  const rows = preview?.rows || EMPTY_ROWS;
  const summary = preview?.summary || EMPTY_SUMMARY;
  const commerces = useMemo(() => groupByCommerce(rows), [rows]);
  const incidents = useMemo(() => buildIncidents(rows, summary), [rows, summary]);
  const fiservFees = useMemo(() => sumAvailable(rows, "merchantFeeAmount"), [rows]);
  const zocoFees = useMemo(() => sumAvailable(rows, "zocoCommercialFee"), [rows]);
  const vat = useMemo(() => sumAvailable(rows, "zocoVat"), [rows]);
  const costs = useMemo(() => sumAvailable(rows, "zocoFinancialCost"), [rows]);
  const advanceCosts = useMemo(() => sumAvailable(rows, "zocoAdvanceCost"), [rows]);
  const debitCreditTax = useMemo(() => sumAvailable(rows, "zocoDebitCreditTax"), [rows]);
  const withholdings = useMemo(() => sumAvailable(rows, "zocoWithholdings"), [rows]);
  const provincial = useMemo(() => sumAvailable(rows, "zocoProvincialWithholding"), [rows]);
  const net = useMemo(() => sumAvailable(rows, "calculatedTotalWithDiscounts"), [rows]);

  const resetResult = () => {
    setSelectedRunId(""); setPreview(null); setError("");
  };

  const changeChannel = (value) => {
    setCanal(value); setRuns([]); resetResult(); setMessage("");
  };

  const changeDate = (event) => {
    setFecha(event.target.value); setRuns([]); resetResult(); setMessage("");
  };

  const consultRuns = async () => {
    resetResult(); setMessage("");
    if (canal === "QR") {
      setRuns([]);
      setMessage("QR todavía no dispone de un servicio de corridas integrado al motor de conciliación.");
      return;
    }
    if (!fecha) {
      setError("Seleccioná una fecha de liquidación.");
      return;
    }
    try {
      setLoadingRuns(true);
      // El endpoint existente limita el historial a 100. La fecha se filtra localmente y no
      // dispara importaciones ni reprocesos.
      const response = await api.obtenerHistorialReconciliation(token, 100);
      const available = (response?.runs || []).filter((run) => String(run.fechaLiquidacion || "").slice(0, 10) === fecha);
      setRuns(available);
      if (!available.length) setMessage("No existen corridas SAS para la fecha seleccionada.");
    } catch (requestError) {
      setRuns([]);
      setError(requestError?.message || "No se pudo consultar el servicio de corridas.");
    } finally {
      setLoadingRuns(false);
    }
  };

  const selectRun = async (runId) => {
    setSelectedRunId(runId); setPreview(null); setError(""); setMessage("");
    try {
      setLoadingPreview(true);
      const response = await api.obtenerPaymentEngine(runId, token);
      if (response?.mode !== "READ_ONLY_PREVIEW" || response?.persisted !== false) {
        throw new Error("El servicio no devolvió un preview de solo lectura verificable.");
      }
      setPreview(response);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo preparar la vista FUR de esta corrida.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const noData = preview && rows.length === 0;
  const incomplete = preview && (
    noData || summary.amountNoGoldenCount > 0 || summary.amountPartialCount > 0
    || summary.amountBlockedCount > 0 || summary.scheduleUnknown > 0
    || summary.duplicateAnticipoRows > 0
  );

  return (
    <main className={`pagos-page${darkMode ? " pagos-page--dark" : ""}`} data-testid="pagos-page">
      <header className="pagos-header">
        <div>
          <p className="pagos-eyebrow">Pagos a Comercios</p><h1>Preparación FUR</h1>
          <p className="pagos-subtitle">Consulta del motor de conciliación. No genera archivos, transferencias ni cambios de estado.</p>
        </div>
        <span className="pagos-mode-badge">Solo lectura</span>
      </header>

      <ProcesamientoSas token={token} persistencia={persistencia} onRegistered={() => setRegistros((value) => value + 1)} />

      <section className="pagos-panel" aria-labelledby="pagos-filtros-title">
        <div className="pagos-section-heading">
          <div><h2 id="pagos-filtros-title">Corrida a consultar</h2><p>Elegí el origen y la fecha de liquidación.</p></div>
          <span className="pagos-status pagos-status--safe">Preview sin persistencia</span>
        </div>
        <div className="pagos-filter-grid">
          <fieldset className="pagos-channel" aria-label="Origen de la liquidación"><legend>Origen</legend><div>
            {["SAS", "QR"].map((value) => (
              <button key={value} type="button" className={canal === value ? "is-active" : ""}
                aria-pressed={canal === value} onClick={() => changeChannel(value)}>{value}</button>
            ))}
          </div></fieldset>
          <label className="pagos-date"><span>Fecha de liquidación</span>
            <input aria-label="Fecha de liquidación" type="date" value={fecha} onChange={changeDate} />
          </label>
          <button type="button" className="pagos-primary" disabled={!fecha || loadingRuns}
            onClick={consultRuns}>{loadingRuns ? "Consultando…" : "Consultar corridas"}</button>
        </div>
        {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}
        {message && <div className="pagos-alert" role="status">{message}</div>}
      </section>

      <section className="pagos-panel" aria-labelledby="pagos-corridas-title">
        <div className="pagos-section-heading"><div><h2 id="pagos-corridas-title">Corridas disponibles</h2>
          <p>{canal}{fecha ? ` · ${formatDate(fecha)}` : " · seleccioná una fecha"}</p></div></div>
        {!runs.length ? (
          <EmptyState>{canal === "QR" ? "No hay una fuente QR integrada." : "Consultá una fecha para ver corridas SAS existentes."}</EmptyState>
        ) : (
          <div className="pagos-run-list">{runs.map((run) => (
            <button type="button" key={run.id} className={selectedRunId === run.id ? "is-selected" : ""}
              onClick={() => selectRun(run.id)} aria-label={`Seleccionar corrida ${run.id}`}>
              <span><strong>{formatDate(run.fechaLiquidacion)}</strong><small>ID {run.id}</small></span>
              <span><strong>{run.cantidadFiserv ?? "No disponible"}</strong><small>operaciones Fiserv</small></span>
              <span className="pagos-status">{labelStatus(run.estado)}</span>
            </button>
          ))}</div>
        )}
        {loadingPreview && <EmptyState>Preparando el preview FUR con el motor existente…</EmptyState>}
      </section>

      <ControlesPrevios token={token} runId={preview ? selectedRunId : ""} runRows={preview ? rows : null}
        persistencia={persistencia} onRegistered={() => setRegistros((value) => value + 1)} />
      <LiquidacionesRegistradas token={token} persistencia={persistencia} refreshKey={registros} />

      {preview && <>
        <div className={`pagos-alert ${incomplete ? "pagos-alert--warning" : "pagos-alert--info"}`} role="status">
          {noData
            ? "La corrida no tiene operaciones disponibles. No hay información suficiente para preparar el FUR."
            : incomplete
            ? "Corrida incompleta: existen datos faltantes o bloqueos. No puede interpretarse como un FUR definitivo."
            : "El motor no informó bloqueos, pero esta vista no aprueba ni autoriza pagos."}
        </div>

        <section className="pagos-summary" aria-label="Resumen de la corrida">
          <SummaryCard label="Operaciones" value={summary.totalRows ?? "No disponible"} detail={`${commerces.length} comercios identificados`} source="Motor de conciliación" />
          <SummaryCard label="Importe bruto" value={formatMoney(summary.grossAmountTotal)} detail="Universo Fiserv del dataset" source="Fuente Fiserv" />
          <SummaryCard label="Arancel Fiserv" value={formatMoney(fiservFees.value)} detail={`${fiservFees.coverage}/${rows.length} operaciones con dato`} source="Fuente Fiserv" />
          <SummaryCard label="Arancel comercial Zoco" value={formatMoney(zocoFees.value)} detail={`${zocoFees.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="IVA Zoco" value={formatMoney(vat.value)} detail={`${vat.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="Costo financiero Zoco" value={formatMoney(costs.value)} detail={`${costs.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="Costo por anticipo" value={formatMoney(advanceCosts.value)} detail={`${advanceCosts.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="Impuesto débito/crédito" value={formatMoney(debitCreditTax.value)} detail={`${debitCreditTax.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="Retenciones totales" value={formatMoney(withholdings.value)} detail={`${withholdings.coverage}/${rows.length} operaciones con golden`} source="Referencia productiva" />
          <SummaryCard label="Ingresos Brutos" value={formatMoney(provincial.value)} detail={`${provincial.coverage}/${rows.length} operaciones con golden`} source="Retención provincial · golden" />
          <SummaryCard label="Neto estimado de control" value={formatMoney(net.value)} detail={`${net.coverage}/${rows.length} operaciones calculables`} source="Depende de base_dashboard" />
        </section>

        <section className="pagos-panel" aria-labelledby="pagos-comercios-title">
          <div className="pagos-section-heading">
            <div><h2 id="pagos-comercios-title">Importes por comercio</h2><p>Agrupación informativa; no constituye una orden de pago.</p></div>
            <SourceBadge golden>Netos: golden productivo</SourceBadge>
          </div>
          <div className="pagos-table-wrap"><table><thead><tr>
            <th>Comercio</th><th>Merchant / terminal</th><th>Operaciones</th><th>Importe bruto</th>
            <th>Neto estimado</th><th>Cobertura neto</th><th>Control</th>
          </tr></thead><tbody>{!commerces.length ? <tr><td colSpan="7"><EmptyState>No hay comercios disponibles en esta corrida.</EmptyState></td></tr> : commerces.map((commerce) => (
            <tr key={commerce.key}><td><strong>{commerce.name}</strong></td>
              <td>{commerce.merchantId}<small className="pagos-cell-note">Terminal {commerce.terminal}</small></td>
              <td>{commerce.operations}</td><td>{formatMoney(commerce.gross)}</td><td>{formatMoney(commerce.net)}</td>
              <td>{commerce.netCoverage}/{commerce.operations}</td><td>
                <span className={`pagos-status ${commerce.blocked ? "pagos-status--warning" : "pagos-status--safe"}`}>
                  {commerce.blocked ? `${commerce.blocked} a revisar` : "Sin bloqueos informados"}
                </span></td></tr>
          ))}</tbody></table></div>
        </section>

        <section className="pagos-panel" aria-labelledby="pagos-operaciones-title">
          <div className="pagos-section-heading"><div><h2 id="pagos-operaciones-title">Detalle de operaciones</h2><p>Elegibilidad, conciliación y control monetario por operación.</p></div></div>
          <div className="pagos-table-wrap"><table><thead><tr>
            <th>Operación</th><th>Comercio</th><th>Conciliación</th><th>Elegibilidad</th>
            <th>Bruto</th><th>Neto estimado</th><th>Validación</th>
          </tr></thead><tbody>{!rows.length ? <tr><td colSpan="7"><EmptyState>No hay operaciones disponibles en esta corrida.</EmptyState></td></tr> : rows.map((row) => (
            <tr key={row.fiservOperationId} className={isBlockedOperation(row) ? "pagos-row--review" : ""}>
              <td>{row.fiservOperationId}<small className="pagos-cell-note">Cupón {row.coupon || "no informado"}</small></td>
              <td>{row.commerceName || "Sin nombre"}<small className="pagos-cell-note">Terminal {row.terminalFiserv || "no informada"}</small></td>
              <td>{labelStatus(row.matchStatus)}<small className="pagos-cell-note">{labelStatus(row.resolutionStatus)}</small></td>
              <td>{labelStatus(row.paymentEligibility)}<small className="pagos-cell-note">{row.eligibilityReason || "Sin observación"}</small></td>
              <td>{formatMoney(row.grossAmount)}</td><td>{formatMoney(row.calculatedTotalWithDiscounts)}</td>
              <td>{labelStatus(row.amountValidationStatus || row.paymentAmountStatus)}</td>
            </tr>
          ))}</tbody></table></div>
        </section>

        <section className="pagos-panel" aria-labelledby="pagos-incidencias-title">
          <div className="pagos-section-heading"><div><h2 id="pagos-incidencias-title">Diferencias, excepciones y bloqueos</h2><p>Cada motivo se muestra sin convertir conciliación en pagabilidad.</p></div></div>
          <div className="pagos-incident-grid">{incidents.map((incident) => (
            <article key={incident.key} className={(incident.count ?? incident.rows.length) ? "has-items" : ""}>
              <div><strong>{incident.label}</strong><span>{incident.count ?? incident.rows.length}</span></div>
              {incident.generalReason && <p>{incident.generalReason}</p>}
              {incident.rows.slice(0, 5).map((row) => (
                <p key={`${incident.key}-${row.fiservOperationId}`}><b>{row.fiservOperationId}</b> · {incident.reason(row)}</p>
              ))}
              {incident.rows.length > 5 && <small>y {incident.rows.length - 5} más</small>}
            </article>
          ))}</div>
        </section>

        <section className="pagos-panel" aria-labelledby="pagos-trazabilidad-title">
          <div className="pagos-section-heading"><div><h2 id="pagos-trazabilidad-title">Trazabilidad del preview</h2><p>Fuentes y alcance de los datos mostrados.</p></div></div>
          <div className="pagos-trace">
            <p><SourceBadge>Autónomo</SourceBadge> Dataset, conciliación, readiness, calendario, elegibilidad y componentes Fiserv.</p>
            <p><SourceBadge golden>Golden productivo</SourceBadge> Arancel Zoco, IVA, costos, retenciones y neto de control.</p>
            <p><strong>Fecha evaluada:</strong> {formatDate(preview.asOf)} ({preview.asOfSource === "RUN_DATE_DEFAULT" ? "fecha de la corrida" : "fecha explícita"}).</p>
            <p><strong>Persistencia:</strong> {preview.persisted === false ? "No. Respuesta READ_ONLY_PREVIEW." : "No verificable"}</p>
          </div>
        </section>
      </>}
    </main>
  );
}
